'use client';
// TODO: above will cause all the subsequent components to be client components - moving to prod, we have to fixate this.

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import type { StoreConfig } from '@multitenancy/config';

interface FeatureToggles {
  showChakraTest: boolean;
  heroWidthMode: 'full' | 'limited' | 'focused'; // full-width, limited-width, or focused with blur
  scrollableCollections: boolean;
  autoRotateHero: boolean;
  productCardAnimation: 'fade' | 'zoom' | 'slide-up' | 'glow';
  ctaButtonStyle: 'bold' | 'outline' | 'gradient' | 'animated';
  promoStyle: 'static' | 'animated' | 'countdown' | 'urgency';
  gridColumns: 2 | 3 | 4 | 'masonry' | 'metro';
  showTrustBadges: boolean;
  showRecentPurchases: boolean;
  showProductReviews: boolean;
  influencerLayout: 'grid' | 'carousel' | 'spotlight';
  newsletterStyle: 'inline' | 'emphasis' | 'minimal';
  categoryGridLayout: 'hidden' | 'tiles' | 'contained';
  categoryTextStyle: 'normal' | 'bold';
  showAnnouncementBar1: boolean;
  showAnnouncementBar2: boolean;
  showAnnouncementBar3: boolean;
}

interface VariantContextType {
  storeConfig: StoreConfig;
  heroVariant: string;
  setHeroVariant: (variant: string) => void;
  headerVariant: string;
  setHeaderVariant: (variant: string) => void;
  footerVariant: string;
  setFooterVariant: (variant: string) => void;
  featureToggles: FeatureToggles;
  setFeatureToggles: (toggles: FeatureToggles | ((prev: FeatureToggles) => FeatureToggles)) => void;
}

const TenantAndVariantContext = createContext<VariantContextType | undefined>(undefined);

export function TenantAndVariantProvider({
  children,
  storeConfig
}: {
  children: ReactNode
  storeConfig: StoreConfig
}) {
  const [heroVariant, setHeroVariant] = useState(storeConfig.heroVariant);
  const [headerVariant, setHeaderVariant] = useState(storeConfig.headerVariant);
  const [footerVariant, setFooterVariant] = useState(storeConfig.footerVariant);
  const [featureToggles, setFeatureToggles] = useState<FeatureToggles>({
    showChakraTest: false,
    heroWidthMode: storeConfig.features?.heroWidthMode || 'full',
    scrollableCollections: false,
    autoRotateHero: false,
    productCardAnimation: storeConfig.features?.productCardAnimation || 'zoom',
    ctaButtonStyle: storeConfig.features?.ctaButtonStyle || 'bold',
    promoStyle: 'static',
    gridColumns: storeConfig.features?.gridColumns || 4,
    showTrustBadges: false,
    showRecentPurchases: false,
    showProductReviews: false,
    influencerLayout: storeConfig.features?.influencerLayout || 'grid',
    newsletterStyle: 'inline',
    categoryGridLayout: storeConfig.features?.categoryGridLayout || 'tiles',
    categoryTextStyle: 'normal',
    showAnnouncementBar1: true,
    showAnnouncementBar2: false,
    showAnnouncementBar3: false,
  });

  // Wrapper functions to handle string types
  const handleSetHeroVariant = (variant: string) => {
    setHeroVariant(variant as 'classic' | 'showcase' | 'minimal');
  };

  const handleSetHeaderVariant = (variant: string) => {
    setHeaderVariant(variant as 'classic' | 'showcase');
  };

  const handleSetFooterVariant = (variant: string) => {
    setFooterVariant(variant as 'classic' | 'showcase');
  };

  return (
    <TenantAndVariantContext.Provider
      value={{
        storeConfig,
        heroVariant,
        setHeroVariant: handleSetHeroVariant,
        headerVariant,
        setHeaderVariant: handleSetHeaderVariant,
        footerVariant,
        setFooterVariant: handleSetFooterVariant,
        featureToggles,
        setFeatureToggles,
      }}
    >
      {children}
    </TenantAndVariantContext.Provider>
  );
}

export function useTenantAndVariant() {
  const context = useContext(TenantAndVariantContext);
  if (context === undefined) {
    throw new Error('useVariant must be used within a VariantProvider');
  }
  return context;
}

/**
 * Hook to access the current store configuration
 * This provides access to store name, theme, sales channel, etc.
 * TODO: this can be upgraded to a server hook since server already has this info.
 */
export function useTenant() {
  const context = useContext(TenantAndVariantContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a VariantProvider');
  }
  return context.storeConfig;
}
